import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { paymentsAPI } from "@/services/api";
import { Wallet, ExternalLink, RefreshCw, ShieldCheck, AlertCircle } from "lucide-react";

const formatNaira = (amount) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const statusBadge = {
  unfunded: "bg-amber-100 text-amber-800",
  funded: "bg-emerald-100 text-emerald-800",
  low: "bg-orange-100 text-orange-800",
  exhausted: "bg-red-100 text-red-800",
  refunded: "bg-gray-100 text-gray-700",
};

/**
 * Escrow funding panel — used in onboarding and campaign detail.
 */
const EscrowFundPanel = ({
  campaignId,
  suggestedAmount = null,
  onFunded = null,
  compact = false,
}) => {
  const [escrow, setEscrow] = useState(null);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(true);
  const [funding, setFunding] = useState(false);

  const loadEscrow = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const data = await paymentsAPI.getEscrow(campaignId);
      setEscrow(data);
      if (!amount && data.minimum_deposit != null) {
        const amountNeeded = Math.max(
          0,
          data.minimum_deposit - (data.available_balance || 0)
        );
        const defaultAmount = amountNeeded > 0 ? amountNeeded : data.minimum_deposit;
        setAmount(String(suggestedAmount || Math.ceil(defaultAmount)));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load escrow details");
    } finally {
      setLoading(false);
    }
  }, [amount, campaignId, suggestedAmount]);

  useEffect(() => {
    loadEscrow();
  }, [loadEscrow]);

  const handleFund = async () => {
    const fundAmount = parseFloat(amount);
    if (!fundAmount || fundAmount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }

    const amountNeeded = Math.max(
      0,
      (escrow?.minimum_deposit || 0) - (escrow?.available_balance || 0)
    );
    if (
      escrow &&
      escrow.available_balance < escrow.minimum_deposit &&
      fundAmount < amountNeeded
    ) {
      toast.error(
        `Minimum deposit is ${formatNaira(amountNeeded || escrow.minimum_deposit)}`
      );
      return;
    }

    setFunding(true);
    try {
      const useDevFund =
        process.env.NODE_ENV === "development" &&
        escrow?.dev_fund_available &&
        !escrow?.paystack_enabled;

      const result = useDevFund
        ? await paymentsAPI.devFundCampaign(campaignId, fundAmount)
        : await paymentsAPI.fundCampaign(campaignId, fundAmount);

      if (useDevFund) {
        toast.success(result.message || "Escrow funded (dev mode)");
        await loadEscrow();
        if (onFunded) onFunded(result);
        return;
      }

      const link = result.payment_url || result.paystack_link || result.paystackLink;
      if (link) {
        window.open(link, "_blank", "noopener,noreferrer");
        toast.success("Complete payment in the Paystack window. This page will update when confirmed.");
      } else {
        toast.message("Payment initiated", { description: result.message || "Check your email for the payment link." });
      }
      if (onFunded) onFunded(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start payment");
    } finally {
      setFunding(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-32" />
        </CardContent>
      </Card>
    );
  }

  if (!escrow) return null;

  const isFunded = escrow.status === "funded" || escrow.status === "low";
  const isTestPaymentMode = escrow.payment_mode === "test";
  const amountNeeded = Math.max(
    0,
    escrow.minimum_deposit - (escrow.available_balance || 0)
  );

  return (
    <Card className={`border-0 shadow-sm ${compact ? "" : "rounded-2xl"}`}>
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-brand-500" />
            </div>
            <div>
              <p className="font-bold">Prove-Request Escrow</p>
              <p className="text-sm text-muted-foreground">
                BRMs are paid only from funds you deposit here
              </p>
            </div>
          </div>
          <Badge className={statusBadge[escrow.status] || statusBadge.unfunded}>
            {escrow.status}
          </Badge>
        </div>

        {isTestPaymentMode && (
          <div className="rounded-xl bg-sky-50 px-3 py-2 text-sm text-sky-900">
            Test payment mode is on. Use a Paystack test card; no real money will
            be charged.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="p-3 rounded-xl bg-gray-50">
            <p className="text-muted-foreground text-xs uppercase font-bold">Balance</p>
            <p className="text-lg font-black">{formatNaira(escrow.available_balance)}</p>
          </div>
          <div className="p-3 rounded-xl bg-gray-50">
            <p className="text-muted-foreground text-xs uppercase font-bold">Min. deposit</p>
            <p className="text-lg font-black">{formatNaira(escrow.minimum_deposit)}</p>
          </div>
          <div className="p-3 rounded-xl bg-gray-50">
            <p className="text-muted-foreground text-xs uppercase font-bold">Conversions left</p>
            <p className="text-lg font-black">{escrow.conversions_remaining ?? 0}</p>
          </div>
          <div className="p-3 rounded-xl bg-gray-50">
            <p className="text-muted-foreground text-xs uppercase font-bold">Per conversion</p>
            <p className="text-lg font-black">{formatNaira(escrow.commission_per_goal)}</p>
          </div>
        </div>

        {isFunded ? (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 text-emerald-800 text-sm">
            <ShieldCheck className="w-4 h-4 shrink-0" />
            <span>Escrow funded — BRMs can promote this Prove-Request.</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 text-amber-900 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>
              Deposit at least {formatNaira(amountNeeded || escrow.minimum_deposit)} to go live.
            </span>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="fund-amount">Amount to deposit (NGN)</Label>
          <Input
            id="fund-amount"
            type="number"
            min={1000}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={String(escrow.minimum_deposit || 10000)}
            className="h-11 rounded-xl"
          />
        </div>

        <div className="flex gap-2">
          <Button
            className="flex-1 h-11 rounded-xl font-bold"
            onClick={handleFund}
            disabled={funding}
          >
            {funding
              ? "Processing…"
              : escrow?.dev_fund_available && !escrow?.paystack_enabled
                ? isFunded
                  ? "Top up escrow (dev)"
                  : "Fund escrow (dev)"
                : isFunded
                  ? "Top up escrow"
                  : isTestPaymentMode
                    ? "Fund via Paystack (test)"
                    : "Fund via Paystack"}
            {escrow?.paystack_enabled !== false && (
              <ExternalLink className="w-4 h-4 ml-2" />
            )}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-11 w-11 rounded-xl"
            onClick={loadEscrow}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default EscrowFundPanel;
